import type { CSSProperties } from "react"
import { CAREERS_MAX_W, CAREERS_INSET } from "@/lib/career/layout"

// ONE footer for the PUBLIC careers site (careers.signalmax.ai). Every public shell renders this
// exact component (PublicIntelShell, home, pricing, FAQ, try) so the footer is identical site-wide:
// same links, same brand line, same aggregate-intelligence disclosure, and the same full-width
// column edges as the header. Replaces three divergent inline footers that had drifted apart.
// Token-driven so it themes light/dark inside the .smx scope. No hooks/server-only deps, so it
// renders safely in both the server (public) pages and the client marketing pages.

type Link = { href: string; label: string }

const PRODUCT: Link[] = [
  { href: "/try", label: "Find my internships" },
  { href: "/pricing", label: "Pricing" },
  { href: "/faq", label: "FAQ" },
  { href: "/guides", label: "Guides" },
  { href: "/login", label: "Sign in" },
]
const INTELLIGENCE: Link[] = [
  { href: "/intelligence", label: "Intelligence" },
  { href: "/intelligence/summer-2027-internships", label: "Summer 2027 Index" },
  { href: "/methodology", label: "Methodology" },
  { href: "/data-sources", label: "Data sources" },
]
const COMPANY: Link[] = [
  { href: "/about", label: "About" },
  { href: "/editorial-standards", label: "Editorial standards" },
  { href: "/corrections", label: "Corrections" },
  { href: "/student-safety", label: "Student safety" },
  { href: "/privacy", label: "Privacy" },
]

const colStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 9, minWidth: 0 }
const headStyle: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 2 }
const linkStyle: CSSProperties = { color: "var(--text-secondary)", textDecoration: "none", fontSize: 13.5, whiteSpace: "nowrap" }

function LinkCol({ title, links }: { title: string; links: Link[] }) {
  return (
    <div style={colStyle}>
      <div style={headStyle}>{title}</div>
      {links.map((l) => (
        <a key={l.href} href={l.href} style={linkStyle}>{l.label}</a>
      ))}
    </div>
  )
}

export function CareersFooter() {
  return (
    <footer style={{ borderTop: "1px solid var(--border-subtle)", background: "var(--surface-sunken)", marginTop: "auto" }}>
      <div
        style={{
          width: "100%",
          maxWidth: CAREERS_MAX_W,
          margin: "0 auto",
          padding: `clamp(28px, 4vw, 48px) ${CAREERS_INSET}`,
          display: "grid",
          gridTemplateColumns: "minmax(240px, 1.4fr) repeat(3, minmax(150px, 1fr))",
          gap: "clamp(24px, 3vw, 48px)",
          alignItems: "start",
        }}
        className="careers-footer-grid"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text-primary)" }}>SignalMax Careers</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>Powered by SignalMax</span>
          </div>
          <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.55, margin: 0, maxWidth: 360 }}>
            Aggregate internship-market intelligence. Figures describe the live tracked index, not the
            full market. Personalized match analysis lives inside the product.
          </p>
        </div>
        <LinkCol title="Product" links={PRODUCT} />
        <LinkCol title="Intelligence" links={INTELLIGENCE} />
        <LinkCol title="Company" links={COMPANY} />
      </div>
      {/* Stack the columns on narrow screens so links never crush together. */}
      <style>{`@media (max-width:720px){.careers-footer-grid{grid-template-columns:1fr !important}}`}</style>
    </footer>
  )
}
